<template>
  <div class="vf-form-field" :data-field-type="field.dataType">
    <label class="vf-form-field-label">
      {{ field.fieldName }}
      <span v-if="field.required" class="required-indicator">*</span>
    </label>
    
    <div class="vf-form-field-content">
      <div class="file-upload-container" v-if="editMode">
        <input
          type="file"
          :accept="field.allowedFileTypes"
          :multiple="field.allowMultipleValues"
          @change="onFileChange"
          :name="fieldName"
          class="form-control-file"
          ref="fileInput" />

        <div v-if="isUploading" class="upload-progress">
          <div class="upload-status">
            <i class="fas fa-spinner fa-spin"></i>
            <span class="upload-text">Uploading {{ uploadProgress.current }} of {{ uploadProgress.total }} file<span v-if="uploadProgress.total > 1">s</span>...</span>
          </div>
        </div>

        <div v-if="uploadedFiles.length > 0" class="uploaded-files">
          <div v-for="(file, index) in uploadedFiles" :key="index" class="file-item">
            <i class="fas fa-file"></i>
            <span class="file-name">{{ file.name }}</span>
            <span class="file-size">({{ formatFileSize(file.size) }})</span>
            <button @click="removeFile(file.uuid)" class="btn btn-sm btn-danger remove-file" type="button">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div v-if="field.allowedFileTypes || field.maxFileSize" class="file-constraints">
          <small class="text-muted">
            <span v-if="field.allowedFileTypes">Allowed types: {{ field.allowedFileTypes }}</span>
            <span v-if="field.allowedFileTypes && field.maxFileSize"> | </span>
            <span v-if="field.maxFileSize">Max size: {{ field.maxFileSize }}MB</span>
          </small>
        </div>
        
        <div v-if="field.description" class="vf-form-field-description" v-html="textToHtml(field.description)"></div>

        <div v-if="errorMessage" class="alert alert-danger" style="margin-top: 10px;">
          {{ errorMessage }}
        </div>
      </div>
      <div v-else>
        <div v-if="field.assets.length > 0" class="uploaded-files">
          <div v-for="(asset, index) in field.assets" :key="index" class="file-item">
            <i class="fas fa-file"></i>
            <span class="file-name"><a :href="asset.url" :title="asset.fileName" target="_blank">{{ asset.fileName }}</a></span>
            <span class="file-size">({{ formatFileSize(asset.fileSizeBytes) }})</span>
          </div>
        </div>
        <div v-else>
          <span class="text-muted">No files uploaded</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { buildFieldName, textToHtml } from '../../Forms/form-helper.js';
  import { uploadFormFile } from '../../../lib/asset-uploader.js';
  import { v4 as uuidv4 } from 'uuid';

  export default {
    name: 'FileFieldRenderer',
    props: {
      field: {
        type: Object,
        required: true,
      },
      propertyId : {
        type: Number,
        required: true
      },
      formId : {
        type: Number,
        required: true
      },
      formResponseId : {
        type: Number,
        required: true
      },
      editMode :{
        type: Boolean,
        required: true
      },
      ensureFormResponse: {
        type: Function,
        required: true
      }
    },
    data() {
      return {
        fieldName : buildFieldName(this.field),
        uploadedFiles: [],
        errorMessage: '',
        isUploading: false,
        uploadProgress: {
          current: 0,
          total: 0
        }
      };
    },
    mounted() {
      // Validate on mount to handle initial state
      this.validateField();
    },
    methods: {
      textToHtml,

      validateField() {
        if (!this.$refs.fileInput) return;

        if (this.field.required) {
          const currentAssets = this.getFieldValue();
          if (!currentAssets || currentAssets.length === 0) {
            this.$refs.fileInput.setCustomValidity('Please upload at least one file.');
          } else {
            this.$refs.fileInput.setCustomValidity('');
          }
        } else {
          this.$refs.fileInput.setCustomValidity('');
        }
      },

      async onFileChange(event) {
        let files = Array.from(event.target.files);
        this.errorMessage = '';

        // Assign a UUID to each file for tracking
        files.forEach(f => {
          f.uuid = uuidv4();
        });

        // Validate file types
        if (this.field.allowedFileTypes) {
          const allowedExtensions = this.field.allowedFileTypes.split(',').map(ext => ext.trim().toLowerCase());
          for (const file of files) {
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(fileExtension)) {
              this.errorMessage = `File type not allowed: ${file.name}. Allowed types: ${this.field.allowedFileTypes}`;
              this.$refs.fileInput.value = '';
              return;
            }
          }
        }

        // Validate file size
        if (this.field.maxFileSize) {
          const maxSizeBytes = this.field.maxFileSize * 1024 * 1024;
          for (const file of files) {
            if (file.size > maxSizeBytes) {
              this.errorMessage = `File too large: ${file.name}. Max size: ${this.field.maxFileSize}MB`;
              this.$refs.fileInput.value = '';
              return;
            }
          }
        }

        // If not allowing multiple, replace existing files
        if (!this.field.allowMultipleValues) {
          files = [files[0]];
          this.uploadedFiles = files;
        } else {
          this.uploadedFiles.push(...files);
        }

        this.$refs.fileInput.value = '';

        // Set up upload progress tracking
        this.isUploading = true;
        this.uploadProgress.total = files.length;
        this.uploadProgress.current = 0;

        try {
          // Ensure a form response exists before uploading (formResponseId may not
          // be set yet if this is the first interaction with the form)
          await this.ensureFormResponse();

          // Upload each file
          for (var index = 0; index < files.length; index++) {
              var file = files[index];

              this.uploadProgress.current = index + 1;

              const uploadResponse = await uploadFormFile(file, this.formId, this.formResponseId, this.field.id);

              var currentAssets = this.getFieldValue();

              currentAssets.push({
                assetId: uploadResponse.assetId,
                uuid: file.uuid
              });

              this.setFieldValue(currentAssets);
          }
        } catch (error) {
          this.errorMessage = 'An error occurred while uploading files. Please try again.';
          console.error('File upload error:', error);
        } finally {
          // Reset upload progress
          this.isUploading = false;
          this.uploadProgress.current = 0;
          this.uploadProgress.total = 0;

          // Validate the field after upload completes
          this.validateField();
        }
      },

      removeFile(uuid) {
        const uploadedFileIndex = this.uploadedFiles.findIndex(f => f.uuid === uuid);

        if(uploadedFileIndex !== -1){
          this.uploadedFiles.splice(uploadedFileIndex, 1);
        }

        var currentAssets = this.getFieldValue();

        const currentAssetsIndex = currentAssets.findIndex(a => a.uuid === uuid);

        if(currentAssetsIndex !== -1){
          currentAssets.splice(currentAssetsIndex, 1);
        }

        this.setFieldValue(currentAssets);

        // Validate the field after file is removed
        this.validateField();
      },

      setFieldValue(newValue) {
        if(!newValue){
          this.field.value = null;
        }else{
          this.field.value = JSON.stringify(newValue);
        }
      },

      getFieldValue(){
        if(!this.field.value){
          return [];
        }else{
          return JSON.parse(this.field.value);
        }
      },

      formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
      }
    },
  };
</script>

<style scoped>
  .vf-field {
    margin-bottom: 20px;
  }

  .vf-field-label {
    display: block;
    font-weight: 600;
    margin-bottom: 5px;
  }

  .vf-field-description {
    color: #6c757d;
    font-size: 0.9em;
    margin-bottom: 8px;
  }

  .required-indicator {
    color: #d9534f;
    margin-left: 3px;
  }

  .file-upload-container {
    margin-top: 5px;
  }

  .upload-progress {
    margin-top: 15px;
    padding: 15px;
    background-color: #e7f3ff;
    border: 1px solid #b3d9ff;
    border-radius: 4px;
  }

  .upload-status {
    display: flex;
    align-items: center;
    color: #0066cc;
    font-weight: 500;
  }

  .upload-status i.fa-spinner {
    margin-right: 10px;
    font-size: 1.2em;
  }

  .upload-text {
    font-size: 0.95em;
  }

  .uploaded-files {
    margin-top: 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
    background-color: #f9f9f9;
  }

  .file-item {
    display: flex;
    align-items: center;
    padding: 8px;
    margin-bottom: 5px;
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 3px;
  }

  .file-item:last-child {
    margin-bottom: 0;
  }

  .file-item i.fa-file {
    color: #5bc0de;
    margin-right: 8px;
  }

  .file-name {
    margin-left: 5px;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-size {
    color: #6c757d;
    font-size: 0.85em;
    margin-left: 8px;
    margin-right: 8px;
  }

  .remove-file {
    padding: 2px 8px;
  }

  .file-constraints {
    margin-top: 8px;
  }

  .text-muted {
    color: #6c757d;
    font-size: 0.875em;
  }

  .alert {
    padding: 10px 15px;
    margin-bottom: 15px;
    border: 1px solid transparent;
    border-radius: 4px;
  }

  .alert-danger {
    background-color: #f2dede;
    border-color: #ebccd1;
    color: #a94442;
  }
</style>
